import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { ProjectCost } from '../../../models/project-vm';
import { UnsavedChangesService } from '../../../../unsaved-changes.service';
import { MessageService } from '../../../services/message.service';

@Component({
  selector: 'app-project-cost-estimates',
  templateUrl: './project-cost-estimates.component.html',
  styleUrl: './project-cost-estimates.component.css'
})
export class ProjectCostEstimatesComponent {
  @Input() categoryId?: string;
  @Input() selectedProjectCost: ProjectCost[] = [];
  @Input() storesCount: number | null | undefined = 0;
  @Input() projectId: number = 0;
  @Input() selectedCategories: number[] = [];
  @Input() thirdPartyAgencies: any[] = [];
  @Input() isSRETeam: boolean = false;

  @Output() notifyProjectCostEstimates = new EventEmitter<{ items: ProjectCost[], totalEstimationCost: number }>();

  selectedAgency: number = 0;
  agencyName = '';
  selectedAgencyCostPerHour: number = 0;

  //To fix Estimated Cost issue
  countLoad: number = 0;
  totalEstimatedCost: number = 0;

  constructor(private unsavedChangesService: UnsavedChangesService, private msgService: MessageService) {
  }

  ngOnInit() {
    if (!this.projectId || this.projectId == 0 || !this.selectedProjectCost || this.selectedProjectCost.length === 0) {
      this.addProjectCostItem(0);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.projectId || this.projectId === 0) {
      if (changes['thirdPartyAgencies'] && this.thirdPartyAgencies !== null) {
        // only one category selected
        if (this.selectedCategories.length === 1 && this.thirdPartyAgencies.length > 0) {
          // set agency dropdown by default to bind which has isDefault = true
          this.setDefaultAgencyWhenSingleCategorySelection();
        } else {
          // multiple categories or no categories selected
          this.selectedAgency = 0;
        }
      }
    }

    if (changes['selectedProjectCost'] && this.selectedProjectCost !== null) {
      this.selectedProjectCost.forEach(pc => {
        if (pc.categoryAgencyID != null && pc.categoryAgencyID > 0) {
          this.selectedAgency = pc.categoryAgencyID;
        }
      });
      this.calculateOnlyNumberOfHours();
      this.calculateEstimate();
    }

    if (changes['storesCount'] && this.storesCount !== null) {
      this.countLoad = this.countLoad + 1;

      if (this.storesCount! == 0) {
        // At Page Load
      }

      if (this.storesCount! == -1) {
        // While Delete
        this.selectedProjectCost.forEach(pc => pc.numberOfStores = 0);
        this.calculateEstimate();
      }

      if (this.storesCount! > 0) {

        if (this.projectId === 0 && this.selectedProjectCost.length === 1) {
          this.selectedProjectCost[0].numberOfStores = this.storesCount ?? undefined;
        }

        if (this.countLoad == 1) {
          this.calculateHours();
        }

        if (this.countLoad > 1) {
          this.countLoad = 0;
        }
      }
    }
  }

  onAnyFieldValueChange(e: boolean) {
    if (e) {
      this.unsavedChangesService.setUnsavedChanges(true);
    }
  }

  onThridPartyAgencyChange(event: any) {
    this.extractCostFromSelectedAgency(this.selectedAgency);
  }

  calculateHours() {
    this.calculateOnlyNumberOfHours();
    this.calculateEstimate();
  }

  formatCurrency(value: number | null | undefined): string {
    if (value === undefined || value === null || isNaN(value)) {
      value = 0;
    }
    return '$' + value.toLocaleString('en-US');
  }

  parseCurrency(value: string): number {
    return parseFloat(value.replace(/[$,]/g, '')) || 0;
  }

  onNumberOfVisitsChange(event: any): void {
    this.calculateEstimate();
  }

  onKeyPress(event: KeyboardEvent): void {
    const inputChar = event.key;
    const inputElement = event.target as HTMLInputElement;
    const currentValue = inputElement.value;

    // Allow control keys (backspace, tab, etc.)
    if (['Backspace', 'Tab', 'Enter', 'Escape', 'ArrowLeft', 'ArrowRight'].includes(inputChar)) {
      return;
    }

    // Get the cursor position
    const cursorPosition = inputElement.selectionStart || 0;

    // Exclude the "$" sign and get the numerical part
    const numericValue = currentValue.replace('$', '');

    // Allow only numeric digits and a single decimal point
    if (/^[0-9]$/.test(inputChar)) {
      // Check if there's a decimal point and limit to two digits after it
      const decimalIndex = numericValue.indexOf('.');
      if (decimalIndex !== -1 && cursorPosition > decimalIndex) {
        const decimalPart = numericValue.substring(decimalIndex + 1);
        if (decimalPart.length >= 2) {
          event.preventDefault(); // Prevent more than 2 digits after the decimal
        }
      }
      return; // Allow the digit input
    }

    // Allow only one decimal point and prevent multiple decimals
    if (inputChar === '.' && !numericValue.includes('.')) {
      return;
    }

    // Prevent any other characters
    event.preventDefault();
  }

  addProjectCostItem(index = 0) {

    let defaultAgencyCostPerHour = 0;
    if (this.selectedAgency) {
      defaultAgencyCostPerHour = this.getCostPerHourByAgency(this.selectedAgency);
    }

    const newItem = new ProjectCost();
    newItem.agencyID = 0;
    newItem.categoryAgencyID = 0;
    newItem.numberOfHours = '';
    newItem.costPerHour = defaultAgencyCostPerHour;
    newItem.hoursPerLocation = 1;
    newItem.estimatedTotalCost = undefined;
    newItem.numberOfVisits = 1;

    if (index === 0) {
      newItem.numberOfStores = this.storesCount!;
    }
    else if (index > 0) {
      let totalProjectCostCalculatedStores = this.getTotalProjectCostStores();

      if (totalProjectCostCalculatedStores >= this.storesCount!) {
        this.msgService.htmlError('Calculated stores cannot exceed total stores.');
        return;
      }

      newItem.numberOfStores = this.storesCount! - totalProjectCostCalculatedStores;

    }
    else {
      newItem.numberOfStores = undefined;
    }

    this.selectedProjectCost?.splice(index, 0, newItem);
    this.calculateEstimate();
  }

  removeProjectCostItem(index: number) {
    this.selectedProjectCost?.splice(index, 1);
    this.calculateHours();
  }

  setDefaultAgencyWhenSingleCategorySelection(): void {
    if (this.selectedCategories.length !== 1) {
      return; // Only applies when exactly one category is selected
    }

    // Check if we already have a valid agency set in selectedProjectCost
    const existingAgencyId = this.selectedProjectCost.find(
      pc => pc.categoryAgencyID && pc.categoryAgencyID > 0
    )?.categoryAgencyID;

    const isExistingValid = existingAgencyId &&
      this.thirdPartyAgencies.some(a => a.categoryAgencyID === existingAgencyId);

    // Find default agency (only if existing is not valid)
    const defaultAgency = !isExistingValid
      ? this.thirdPartyAgencies.find(a => a.isDefault)
      : null;

    // Decide which agency to use
    const agencyToSet = isExistingValid
      ? existingAgencyId!
      : defaultAgency?.categoryAgencyID;

    if (!agencyToSet) {
      return; // No valid agency found
    }

    // Apply to component state
    this.selectedAgency = agencyToSet;

    this.selectedProjectCost.forEach(pc => pc.categoryAgencyID = agencyToSet);

    // Extract cost details from selected agency
    this.extractCostFromSelectedAgency(agencyToSet);
  }

  private getCostPerHourByAgency(agencyId: any): number {
    return this.thirdPartyAgencies.find(a => a.categoryAgencyID === agencyId)?.costPerHour || 0;
  }

  extractCostFromSelectedAgency(selAgency: any): void {
    const costPerHour = this.getCostPerHourByAgency(selAgency);

    this.selectedAgencyCostPerHour = costPerHour;

    this.selectedProjectCost.forEach(pc => {
      Object.assign(pc, {
        agencyID: selAgency,
        categoryAgencyID: selAgency,
        costPerHour
      });
    });

    this.calculateEstimate();
  }

  onCostPerHourChange(event: any, index: number): void {
    this.selectedProjectCost[index].costPerHour = this.parseCurrency(event.target.value);
    this.calculateEstimate();
  }

  onCostOfShippingStoreChange(event: any, index: number): void {
    this.selectedProjectCost[index].costOfShippingStore = this.parseCurrency(event.target.value);
    this.calculateEstimate();
  }

  calculateEstimate(): void {
    if (!this.selectedProjectCost || this.selectedProjectCost.length === 0) {
      return;
    }

    let grandTotal = 0;

    this.selectedProjectCost.forEach(costItem => {
      let nos = costItem.numberOfStores || 0;
      let cph = costItem.costPerHour || 0;
      let sosc = costItem.costOfShippingStore || 0;
      let nov = costItem.numberOfVisits || 0;

      // New cost-per-hour logic
      let costPerHourTotal = 0;
      if (costItem.hoursPerLocation == null) {
        costPerHourTotal = cph; // take costPerHour directly
      } else {
        costPerHourTotal = (costItem.hoursPerLocation || 0) * nos * cph;
      }

      // Shipping cost
      let shippingCostTotal = nos * sosc;

      // Total
      let totalCost = costPerHourTotal + shippingCostTotal;

      if (nov > 0) {
        totalCost *= nov;
      }

      costItem.estimatedTotalCost = parseFloat(totalCost.toFixed(2));

      grandTotal += costItem.estimatedTotalCost;
    });

    // Save grand total
    this.totalEstimatedCost = parseFloat(grandTotal.toFixed(2));

    this.notifyProjectCostEstimates.emit({
      items: this.selectedProjectCost,
      totalEstimationCost: this.totalEstimatedCost
    });
  }

  calculateOnlyNumberOfHours(): void {
    if (this.selectedProjectCost && this.selectedProjectCost.length > 0) {
      this.selectedProjectCost.forEach(pc => {
        if (pc.hoursPerLocation != null && pc.numberOfStores != null) {
          const result = pc.hoursPerLocation * pc.numberOfStores;
          pc.numberOfHours = (Math.round(result * 100) / 100).toString();
        } else {
          pc.numberOfHours = undefined;
        }
      });
    }
  }

  // validateNoOfVisitsAndCalculateHours(event: Event, row: any): void {
  //   const inputElement = event.target as HTMLInputElement;
  //   let value = +inputElement.value;

  //   if (value <= 0) {
  //     inputElement.value = '';
  //     row.numberOfVisits = undefined;
  //   } else {
  //     row.numberOfVisits = value;
  //   }

  //   this.calculateHoursForRow(row);
  // }

  // calculateHoursForRow(row: any): void {
  //   if (row.hoursPerLocation != null && row.numberOfStores != null) {
  //     const result = row.hoursPerLocation * row.numberOfStores;
  //     row.numberOfHours = (Math.round(result * 100) / 100).toString();
  //   } else {
  //     row.numberOfHours = undefined;
  //   }
  // }

  getTotalProjectCostStores(): number {
    return this.selectedProjectCost.reduce((sum, pc) => sum + (pc.numberOfStores || 0), 0);
  }

  getTotalHours(): number | string {
    const total = this.selectedProjectCost.reduce(
      (sum, pc) =>
        sum +
        ((pc.numberOfStores || 0) *
          (pc.hoursPerLocation || 0) *
          (pc.numberOfVisits || 0)),
      0
    );

    return total === 0 ? '' : total;
  }


  getTotalCostPerHour(): number {
    return this.selectedProjectCost.reduce((sum, pc) => {
      const numberOfStores = pc.numberOfStores || 0;
      const costPerHour = pc.costPerHour || 0;

      if (pc.hoursPerLocation == null) {
        // hoursPerLocation missing → take costPerHour directly
        return sum + costPerHour;
      } else {
        return sum + (numberOfStores * pc.hoursPerLocation * costPerHour);
      }
    }, 0);
  }

  getTotalShippingCost(): number {
    return this.selectedProjectCost.reduce(
      (sum, pc) => sum + ((pc.numberOfStores || 0) * (pc.costOfShippingStore || 0)),
      0
    );
  }

  getTotalVisits(): number {
    return this.selectedProjectCost.reduce((sum, pc) => sum + (pc.numberOfVisits || 0), 0);
  }

  restrictZeroAndDotInput(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;

    // Block decimal point (.)
    if (event.key === '.' || event.key === ',') {
      event.preventDefault();
      return;
    }

    // Block "0" if it is the first character
    if (event.key === '0' && input.value === '') {
      event.preventDefault();
      return;
    }
  }


  // Prevent user typing only "0" as the value
  restrictZeroOnly(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;

    // If the first digit is 0 and no decimal point is typed yet → block
    if (event.key === '0' && input.value === '') {
      event.preventDefault();
    }
  }


}
